import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Paperclip, Upload, Trash2, Download, File, Image, Loader2 } from 'lucide-react';
import { customersAPI } from '../lib/api';
import { toast } from 'sonner';

const FILE_TYPE_ICONS = {
  image: Image,
  document: File,
};

function formatFileSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function CustomerAttachmentsCard({ customerId }) {
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const fileRef = useRef(null);

  const fetchAttachments = async () => {
    try {
      const res = await customersAPI.listAttachments(customerId);
      setAttachments(res.data || []);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAttachments(); }, [customerId]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum is 10MB.');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await customersAPI.uploadAttachment(customerId, formData);
      toast.success('Attachment uploaded');
      await fetchAttachments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleDelete = async (attId) => {
    setDeleting(attId);
    try {
      await customersAPI.deleteAttachment(customerId, attId);
      setAttachments(prev => prev.filter(a => a.id !== attId));
      toast.success('Attachment deleted');
    } catch {
      toast.error('Delete failed');
    } finally {
      setDeleting(null);
    }
  };

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  return (
    <Card data-testid="customer-attachments-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
            <Paperclip className="w-4 h-4" /> Attachments
            {attachments.length > 0 && (
              <Badge variant="secondary" className="text-xs ml-1">{attachments.length}</Badge>
            )}
          </CardTitle>
          <div>
            <input
              ref={fileRef}
              type="file"
              className="hidden"
              accept=".jpg,.jpeg,.png,.webp,.pdf,.doc,.docx,.xls,.xlsx,.csv"
              onChange={handleUpload}
              data-testid="attachment-file-input"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              data-testid="upload-attachment-btn"
            >
              {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}
              Upload
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-2.5 py-1 animate-pulse">
            {[1, 2].map(i => (
              <div key={i} className="flex items-center gap-3 px-2 py-1.5">
                <div className="w-4 h-4 rounded bg-muted shrink-0" />
                <div className="flex-1 space-y-1.5">
                  <div className="h-3.5 bg-muted rounded w-2/3" />
                  <div className="h-2.5 bg-muted rounded w-1/3" />
                </div>
              </div>
            ))}
          </div>
        ) : attachments.length === 0 ? (
          <div className="flex flex-col items-center py-6 text-center" data-testid="attachments-empty-state">
            <div className="w-10 h-10 rounded-full bg-muted/60 flex items-center justify-center mb-3">
              <Paperclip className="w-5 h-5 text-muted-foreground/50" />
            </div>
            <p className="text-sm font-medium mb-1">No attachments yet</p>
            <p className="text-xs text-muted-foreground max-w-[280px] mb-1">
              Upload contracts, permits, photos, or other documents related to this customer.
            </p>
            <p className="text-[11px] text-muted-foreground/60 mb-4">PDF, DOC, XLS, CSV, JPG, PNG (Max 10MB)</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              data-testid="empty-upload-btn"
            >
              <Upload className="w-3.5 h-3.5 mr-1.5" /> Upload Attachment
            </Button>
          </div>
        ) : (
          <div className="space-y-1.5">
            {attachments.map(att => {
              const Icon = FILE_TYPE_ICONS[att.file_type] || File;
              return (
                <div key={att.id} className="flex items-center gap-3 py-1.5 px-2 rounded hover:bg-muted/50 group" data-testid={`attachment-row-${att.id}`}>
                  <Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm truncate">{att.file_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatFileSize(att.file_size)}
                      {att.uploaded_by_name && <> &middot; {att.uploaded_by_name}</>}
                      {att.created_at && <> &middot; {new Date(att.created_at).toLocaleDateString()}</>}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      onClick={() => window.open(`${backendUrl}${att.file_url}`, '_blank')}
                      data-testid={`download-attachment-${att.id}`}
                    >
                      <Download className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                      onClick={() => handleDelete(att.id)}
                      disabled={deleting === att.id}
                      data-testid={`delete-attachment-${att.id}`}
                    >
                      {deleting === att.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
